import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import PixelMark from "./PixelMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscribeForm({ source = "site" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("That email doesn't look right — mind checking it?");
      return;
    }
    setState("loading");
    try {
      const res = await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        source,
      });
      setDone(true);
      if (res.data?.already_subscribed) {
        toast("You're already on the list — we've got you.");
      } else {
        toast.success("You're on the list. Talk soon.");
      }
    } catch {
      toast.error("Couldn't save that — try again in a moment.");
      setState("idle");
    }
  };

  return (
    <div className="mt-9 max-w-md" data-testid="subscribe-form">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="done"
            data-testid="subscribe-success"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4 rounded-xl border-2 border-ink bg-white px-6 py-5 shadow-chunk"
          >
            <PixelMark className="h-8 w-8 shrink-0 text-pastel-pink" />
            <div>
              <p className="font-serif text-xl italic text-ink">
                You're on the list.
              </p>
              <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-stone-500">
                We'll email you the moment it's live
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col gap-3 sm:flex-row"
          >
            <input
              data-testid="subscribe-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.com"
              aria-label="Email address"
              className="flex-1 rounded-xl border-2 border-ink bg-white px-5 py-3.5 text-base text-ink placeholder:text-stone-400 focus:outline-none"
            />
            <button
              data-testid="subscribe-submit"
              type="submit"
              disabled={state === "loading"}
              className="rounded-xl border-2 border-ink bg-pastel-pink px-6 py-3.5 font-display text-sm font-extrabold uppercase tracking-tight text-ink shadow-chunk transition-all duration-200 hover:-translate-y-0.5 hover:shadow-chunk-lg disabled:opacity-50"
            >
              {state === "loading" ? "Saving…" : "Notify me"}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
